import { useMemo, useRef, useState } from 'react';
import { metres } from '@day';
import { useStore } from '../app/store';
import * as act from '../app/actions';
import { backToDay, discardPlan, ui } from '../app/ui';
import { ago, inMinutes, length, plural, time, two } from '../app/words';
import { routeTitle, type Model, type Person } from '../data/model';
import { newSession } from '../data/maps';
import { findAddress } from '../data/find';
import { estimates, type Hint } from '../data/routing';
import { extraTime, loadOf, loadOfAll, loadList, packagesOf, type RunStop } from '../data/runs';
import { vehicleWords } from './Live';
import { track } from '../data/analytics';
import { openDialog } from '../app/dialogs';
import { isShortLink, pinLabel, readPin, shortCode } from '../data/pins';
import { Av, EmptyHelp, nearly } from '../ui/bits';
import { firstPart, LookupRows, placeOf, recentPlaces, useLookup, type LookedPlace, type Place } from '../ui/Lookup';
import { Icon } from '../ui/Icon';
import { toast } from '../ui/Overlays';

/**
 * A new route, planned in the list beside the map (control-room-web.html `planView`): stops from the business's own
 * routes first (free), a customer's pin (no lookup), then a lookup through Routr's server (Geocoding first, Places only
 * when that finds nothing usable, as the phone orders them); then who takes it, and Send.
 */

const HINTS: { k: Hint; l: string; tip: string }[] = [
  { k: 'auto', l: 'Auto', tip: 'Routr chooses where it goes' },
  { k: 'first', l: 'First', tip: 'Always first' },
  { k: 'last', l: 'Last', tip: 'Always last' },
];

export function PlanView({ m }: { m: Model }) {
  const s = useStore(ui);
  const plan = s.plan!;
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [sorting, setSorting] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  /** What the search box adds: a stop, or the route's start. */
  const [mode, setMode] = useState<'stop' | 'start'>('stop');
  const [dragging, setDragging] = useState<string | null>(null);
  const session = useRef(newSession());
  const field = useRef<HTMLInputElement>(null);
  const places = useMemo(() => recentPlaces(m), [m.data.history, m.data.routes, m.today, m.zone]);
  const start = act.planStart(m, plan);
  const text = q.trim();
  const ql = text.toLowerCase();
  const taken = new Set(plan.stops.map((x) => x.address));
  const hits = ql.length >= 2 ? places.filter((p) => !taken.has(p.address) && `${p.address}`.toLowerCase().includes(ql)).slice(0, 5) : [];
  const pin = text ? readPin(q, start) : null;
  const short = isShortLink(q);
  const country = m.business?.country?.toLowerCase();
  // A saved or recent place, a pin or a short link answers without Google; anything else is looked up as typing pauses.
  const found = useLookup(text, text.length >= 4 && !pin && !short && !hits.length, country, session, m.shop);
  const set = (next: Partial<act.Plan>) => ui.set({ plan: { ...plan, ...next } });
  const setStops = (stops: act.PlanStop[], more: Partial<act.Plan> = {}) => set({ stops, ...more });
  const clear = () => {
    setQ('');
    session.current = newSession();
    setTimeout(() => field.current?.focus(), 0);
  };
  const add = (p: Place, pinned = false) => {
    if (mode === 'start') {
      set({ start: { label: pinned ? pinLabel({ lat: p.lat, lng: p.lng, kind: 'coordinates' }) : firstPart(p.address), lat: p.lat, lng: p.lng }, timed: null });
      setMode('stop');
      track('live_action', { action: 'plan_start_changed', via: pinned ? 'pin' : p.google ? 'lookup' : 'recent' });
      return clear();
    }
    // What was typed is the owner's own wording of the address (what may leave Routr), only for a Google pick typed out:
    // never a few letters that found a place from the routes, a "Did you mean", or a pin (intake_sheet.dart, PastPlace).
    const writtenAddress = p.google && !pinned && text.length >= 6 ? text : undefined;
    setStops([...plan.stops, { id: crypto.randomUUID(), address: p.address, writtenAddress, area: p.area, lat: p.lat, lng: p.lng, placeId: p.placeId, houseNumber: p.houseNumber, precision: pinned ? 'exact' : p.precision, googleUntil: p.googleUntil ?? null, pin: pinned, name: p.name }], { timed: null });
    clear();
  };
  const pick = async (p: LookedPlace) => {
    setBusy(true);
    const got = await placeOf(p, session.current);
    setBusy(false);
    if (typeof got === 'string') return toast(got);
    add(got);
  };
  /** One of the business's places as the start: the first is where routes start (on the map once a route left from it). */
  const startAt = async (i: number) => {
    const bp = m.business?.places?.[i];
    if (!bp) return;
    setStartOpen(false);
    if (i === 0 && m.shop) return set({ start: undefined, timed: null });
    const f = await findAddress(bp.address, { country: m.business?.country });
    if (!f.place) return toast(f.error ?? `Couldn’t find ${bp.label} on the map: try its address in the search`);
    set({ start: { label: bp.label, lat: f.place.lat, lng: f.place.lng }, timed: null });
  };
  const ordered = act.planOrder(m, plan);
  const timed = act.planTimed(m, plan);
  const sortedNow = !!plan.timed && plan.timed.order.join() === ordered.map((x) => x.id).join();
  const ifNow = estimates(timed, m.now, 180);
  const sort = async () => {
    setSorting(true);
    try {
      ui.set({ plan: await act.sortPlan(plan) });
    } finally {
      setSorting(false);
    }
  };
  /** A new order the owner made (drag, Reverse): kept as it is when it's sent, timed then. */
  const reorder = (ids: string[]) => set({ order: ids, timed: null });
  const rows = whoTakes(m, plan);
  const closest = rows.find((x) => !x.busy && x.km != null);
  const to = plan.to ? m.person(plan.to) : null;
  const look = found && found !== 'looking' && found.q === text ? found : null;
  return (
    <>
      <div className="lhead">
        <div className="row">
          <button className="iconbtn sm" data-tip="Back to today · Esc" aria-label="Back" onClick={backToDay}>
            <Icon name="arrowLeft" size="lg" />
          </button>
          <span className="title grow">New route</span>
          {plan.stops.length > 0 && (
            <button className="btn sm" onClick={() => discardPlan((msg, undo) => toast(msg, { undo: () => void undo() }))}>
              Discard
            </button>
          )}
        </div>
        <div className="startrow">
          <Icon name="storefront" />
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="cap">Starts at</div>
            <b className="ellipsis">{start ? start.label : 'Wherever the courier is'}</b>
          </div>
          <button className="mini" onClick={() => setStartOpen((o) => !o)} aria-expanded={startOpen}>
            Change
          </button>
        </div>
        {startOpen && (
          <div className="rows startpick">
            {(m.business?.places ?? []).map((bp, i) => (
              <div key={bp.label + i} className="item" role="button" tabIndex={0} onClick={() => void startAt(i)}>
                <Icon name="storefront" />
                <div className="grow">
                  <b>{bp.label}</b>
                  <div className="cap ellipsis">{bp.address}</div>
                </div>
              </div>
            ))}
            <div className="item" role="button" tabIndex={0} onClick={() => (set({ start: null, timed: null }), setStartOpen(false))}>
              <Icon name="crosshair" />
              <div className="grow">
                <b>Wherever the courier is</b>
                <div className="cap">Their phone starts it from where they are</div>
              </div>
            </div>
            <div className="item" role="button" tabIndex={0} onClick={() => (setMode('start'), setStartOpen(false), setTimeout(() => field.current?.focus(), 0))}>
              <Icon name="magnifyingGlass" />
              <div className="grow">
                <b>Another address or a pin</b>
                <div className="cap">Type it in the search below</div>
              </div>
            </div>
          </div>
        )}
        <label className="row backrow">
          <input type="checkbox" checked={!!plan.back} onChange={(e) => set({ back: e.target.checked, timed: null })} disabled={!start} />
          <span className="cap">{start ? `Back to ${start.label} at the end` : 'Back to start: set a start first'}</span>
        </label>
        {mode === 'start' && (
          <div className="chip fchip" style={{ marginTop: 10 }}>
            Choosing where it starts
            <button data-tip="Add stops instead" aria-label="Add stops instead" onClick={() => setMode('stop')}>
              <Icon name="x" size="sm" />
            </button>
          </div>
        )}
        <div className="row" style={{ marginTop: 10 }}>
          <label className="field grow">
            <Icon name="magnifyingGlass" />
            <input
              ref={field}
              id="planq"
              placeholder={mode === 'start' ? 'Where it starts: an address, a pin, a Maps link' : 'Add a stop: an address, a pin, a Maps link'}
              value={q}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                if (pin) return add({ address: pinLabel(pin), area: '', lat: pin.lat, lng: pin.lng }, true);
                if (hits[0]) return add(hits[0]);
                if (look?.places[0]) void pick(look.places[0]);
              }}
              aria-label={mode === 'start' ? 'Where the route starts' : 'Add a stop'}
            />
          </label>
        </div>
        {text && (
          <div className="rows" style={{ marginTop: 6 }}>
            {pin && (
              <div className="item hot" role="button" tabIndex={0} onClick={() => add({ address: pinLabel(pin), area: '', lat: pin.lat, lng: pin.lng }, true)}>
                <Icon name="mapPinFill" />
                <div className="grow">
                  <b>{pinLabel(pin)}</b>
                  <div className="cap">The customer’s own pin · no lookup</div>
                </div>
                <span className="kbd">↵</span>
              </div>
            )}
            {short && !pin && <div className="empty cap">That’s a short Maps link, which only opens in a browser: open it, then paste the full address bar or the coordinates here.</div>}
            {hits.map((p, i) => (
              <div key={p.address} className={`item${!pin && i === 0 ? ' hot' : ''}`} role="button" tabIndex={0} onClick={() => add(p)}>
                <Icon name="mapPin" />
                <div className="grow">
                  <b>{firstPart(p.address)}</b>
                  <div className="cap">{p.area} · from your routes</div>
                </div>
                {!pin && i === 0 ? <span className="kbd">↵</span> : <Icon name="plus" className="ink3" />}
              </div>
            ))}
            <LookupRows text={text} found={found} placing={busy} onPick={(p) => void pick(p)} />
            {look && !look.places.length && (
              <EmptyHelp
                q={look.error ? undefined : look.q}
                title={look.error}
                tries={[
                  ...nearly(look.q, places, (p) => p.address).map((p) => ({ icon: 'mapPin', label: firstPart(p.address), note: `${p.area} · from your routes`, act: `place:${p.address}` })),
                  { icon: 'clipboardText', label: 'Paste the customer’s pin', note: 'A Google Maps link, a plus code or coordinates', act: 'clear' },
                  { icon: 'mapTrifold', label: 'Drop a pin', note: 'Right-click the spot on the map: Add to the new route', act: 'clear' },
                ]}
                onTry={(a) => {
                  if (a.startsWith('place:')) {
                    const p = places.find((x) => x.address === a.slice(6));
                    if (p) add(p);
                  } else clear();
                }}
              />
            )}
          </div>
        )}
      </div>
      <div className="lsec">
        <span className="eyebrow">Stops</span>
        <span className="cnt">{plan.stops.length}</span>
        <span className="grow" />
        {plan.stops.length > 1 && !act.planRuns(m, plan) && (
          <>
            <button className="mini" disabled={sorting} data-tip="Google chooses the order, through Routr’s server; First, Last and numbers stay, a pickup stays before its delivery" onClick={() => void sort()}>
              <Icon name="path" size="sm" />
              {sorting ? 'Sorting…' : sortedNow ? 'Sorted' : 'Sort'}
            </button>
            <button className="mini" data-tip="The same stops the other way round" onClick={() => reorder(act.planOrder(m, { ...plan, order: [...ordered].reverse().map((x) => x.id), timed: null }).map((x) => x.id))}>
              <Icon name="arrowsDownUp" size="sm" />
              Reverse
            </button>
          </>
        )}
      </div>
      {plan.stops.length > 0 && (
        <div className="cap" style={{ padding: '0 16px 6px' }} data-tip={timed.method === 'routesApi' ? 'Google’s times, through Routr’s server, and 3 minutes at each stop' : 'Straight lines at 30 km/h, and 3 minutes at each stop: Sort asks Google'}>
          If it leaves now: done about {time(ifNow.finish, m.zone)}
          {plan.back && start ? `, back at ${start.label}` : ''} · {length(timed.distanceM)} · {inMinutes(ifNow.finish - m.now)}
          {timed.method === 'routesApi' ? '' : ' · by straight lines'}
        </div>
      )}
      <RunsOffer m={m} plan={plan} />
      {act.planRuns(m, plan) ? (
        <RunsList m={m} plan={plan} />
      ) : plan.stops.length ? (
        <div className="stops">
          {ordered.map((x, i) => (
            <div
              key={x.id}
              className={`stop plan${dragging === x.id ? ' picked' : ''}`}
              style={{ '--c': 'var(--ink)', '--on': 'var(--surface)' } as React.CSSProperties}
              draggable
              onDragStart={(e) => {
                setDragging(x.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragEnd={() => setDragging(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!dragging || dragging === x.id) return;
                const ids = ordered.map((y) => y.id).filter((id) => id !== dragging);
                ids.splice(ids.indexOf(x.id), 0, dragging);
                reorder(ids);
                setDragging(null);
              }}
            >
              <span className="n">{two(i + 1)}</span>
              <div style={{ minWidth: 0 }}>
                <div className="a ellipsis">{firstPart(x.address)}</div>
                <div className="s ellipsis">{[x.area, x.name, x.phone].filter(Boolean).join(' · ') || (x.pin ? `The customer’s pin · ${shortCode(x.lat, x.lng)}` : ' ')}</div>
                <div className="row" style={{ gap: 6, marginTop: 4 }}>
                  <span className="tpill" data-tip="An estimate, if the route leaves now">about {time(ifNow.arrive[i], m.zone)}</span>
                  <span className="seg mini-seg" role="group" aria-label="Where it goes in the order">
                    {HINTS.map((h) => (
                      <button key={h.k} aria-pressed={x.place == null && (x.hint ?? 'auto') === h.k} data-tip={h.tip} onClick={() => setStops(plan.stops.map((y) => (y.id === x.id ? { ...y, hint: h.k, place: undefined } : y)), { timed: null })}>
                        {h.l}
                      </button>
                    ))}
                  </span>
                  <input
                    key={`${x.place ?? ''}:${i}`}
                    className="planplace"
                    placeholder="No."
                    aria-label="Its number in the order"
                    data-tip="Type its number in the order: Sort and Reverse fit the rest round it"
                    inputMode="numeric"
                    maxLength={3}
                    defaultValue={x.place != null ? i + 1 : ''}
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                    onBlur={(e) => {
                      const n = Number.parseInt(e.target.value, 10);
                      const place = n >= 1 ? n : undefined;
                      if (place === x.place || (place != null && x.place != null && place === i + 1)) return;
                      setStops(plan.stops.map((y) => (y.id === x.id ? { ...y, place, hint: place != null ? 'auto' : y.hint } : y)), { timed: null });
                    }}
                  />
                </div>
                {editing === x.id ? (
                  <div className="planedit" onClick={(e) => e.stopPropagation()}>
                    <input placeholder="Customer’s name" defaultValue={x.name} onBlur={(e) => setStops(plan.stops.map((y) => (y.id === x.id ? { ...y, name: e.target.value.trim() || undefined } : y)))} autoComplete="off" autoCorrect="off" spellCheck={false} />
                    <input placeholder="Their phone" defaultValue={x.phone} inputMode="tel" onBlur={(e) => setStops(plan.stops.map((y) => (y.id === x.id ? { ...y, phone: e.target.value.trim() || undefined } : y)))} />
                    <input placeholder="A note for the courier" defaultValue={x.notes} onBlur={(e) => setStops(plan.stops.map((y) => (y.id === x.id ? { ...y, notes: e.target.value.trim() || undefined } : y)))} />
                    <button className="mini" onClick={() => setEditing(null)}>
                      Done
                    </button>
                  </div>
                ) : (
                  (!x.from || x.copied) && (
                    <button className="link cap" style={{ marginTop: 2 }} onClick={() => setEditing(x.id)}>
                      {x.name || x.phone ? 'Change the customer' : 'Add the customer'}
                    </button>
                  )
                )}
              </div>
              <button className="iconbtn sm" data-tip="Take it off" aria-label="Take it off" onClick={() => setStops(plan.stops.filter((y) => y.id !== x.id), { order: plan.order?.filter((id) => id !== x.id) ?? null, timed: null })}>
                <Icon name="x" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty cap" style={{ paddingTop: 0 }}>
          Type an address, paste a customer’s pin, or right-click the map. Sort asks Google for the best order; otherwise Routr sorts the stops when it sends them.
        </div>
      )}
      <div className="lsec">
        <span className="eyebrow">Who takes it</span>
      </div>
      <div className="rows">
        {rows.map((x) => (
          <div
            key={x.p.uid}
            className="item"
            role="button"
            tabIndex={0}
            aria-pressed={plan.to === x.p.uid}
            style={plan.to === x.p.uid ? { background: 'var(--select)' } : undefined}
            onClick={() => set({ to: plan.to === x.p.uid ? null : x.p.uid })}
          >
            <Av p={x.p} />
            <div className="grow">
              <b>{x.p.first}</b>
              <div className="cap">{x.line}</div>
            </div>
            {x === closest && <span className="chip pos">Closest</span>}
            {plan.to === x.p.uid && <Icon name="check" size="lg" />}
          </div>
        ))}
        {!rows.length && (
          <EmptyHelp
            title="No couriers in your business yet."
            tries={[{ icon: 'userPlus', label: 'Invite a courier', note: 'A link they open on their phone', act: 'invite' }]}
            onTry={() => openDialog({ kind: 'invite' })}
          />
        )}
      </div>
      <div className="lhead" style={{ paddingTop: 14 }}>
        <label className="field" style={{ marginBottom: 10 }}>
          <Icon name="path" />
          <input placeholder={to ? act.runName(m, to.uid) : 'Name it (or Routr names it by the time of day)'} value={plan.name} onChange={(e) => set({ name: e.target.value })} aria-label="The route’s name" />
        </label>
        <button className="btn primary" style={{ width: '100%' }} disabled={!plan.stops.length || !to} onClick={() => act.sendPlan(plan)}>
          <Icon name="paperPlaneTilt" />
          {to ? `Send to ${to.first}` : 'Pick who takes it'}
          {plan.stops.length ? ` · ${plural(plan.stops.length, 'stop')}` : ''}
        </button>
        <div className="cap" style={{ marginTop: 8, textAlign: 'center' }}>
          {start ? `It starts at ${start.label}. ` : 'It starts wherever they are. '}They see it on their map and start it when they’re ready. Customers hear nothing until then.
        </div>
      </div>
    </>
  );
}

/** The stops' ids, sorted: what "Keep one run" is kept against. */
const keyOf = (plan: act.Plan) => plan.stops.map((x) => x.id).sort().join(',');

/**
 * Split into runs (runs.dart, the phone's Review offer): when the stops need more room than the courier's vehicle carries,
 * Routr offers to split them into runs from where the route starts, and never splits by itself.
 */
function RunsOffer({ m, plan }: { m: Model; plan: act.Plan }) {
  const to = plan.to ? m.person(plan.to) : null;
  const cap = act.capacityOf(m, plan);
  const stops = act.runStopsOf(plan);
  const load = loadOfAll(stops);
  const start = act.planStart(m, plan);
  const set = (next: Partial<act.Plan>) => ui.set({ plan: { ...plan, ...next } });
  if (!to || cap == null || plan.stops.length < 2) return null;
  if (plan.runs) {
    if (act.planRuns(m, plan)) return null;
    return (
      <div className="runoffer">
        <Icon name="truck" />
        <div className="grow">
          <b>{start ? 'These fit in one run now' : 'Runs load where the route starts'}</b>
          <div className="cap">{start ? 'Nothing to split.' : 'Set where it starts above (not “wherever the courier is”), and they load there between runs.'}</div>
        </div>
        <button className="mini" onClick={() => set({ runs: null })}>
          One run
        </button>
      </div>
    );
  }
  if (load <= cap || plan.keptOne === keyOf(plan)) return null;
  const runs = start ? act.planRuns(m, { ...plan, runs: { n: null } }) : null;
  return (
    <div className="runoffer">
      <Icon name="truck" />
      <div className="grow">
        <b>
          {plural(load, 'parcel')} for {to.first}’s {vehicleWords(to.courier.vehicle).toLowerCase()}, which carries about {cap}
        </b>
        <div className="cap">
          {runs
            ? `Split into ${runs.runs.length} runs from ${start!.label}, each loaded there: about ${inMinutes(extraTime(runs.runs, runs.depot) * 1000)} longer than one run.`
            : 'Set where it starts first (not “wherever the courier is”): runs load there.'}
        </div>
        <div className="acts">
          <button className="mini strong" disabled={!runs} onClick={() => set({ runs: { n: null }, order: null, timed: null })}>
            <Icon name="truck" size="sm" />
            Split into runs
          </button>
          <button className="mini" onClick={() => set({ keptOne: keyOf(plan) })}>
            Keep one run
          </button>
        </div>
      </div>
    </div>
  );
}

/** A plan split into runs: each run with its stops in order from the load point, and its load list (last drop first). */
function RunsList({ m, plan }: { m: Model; plan: act.Plan }) {
  const rp = act.planRuns(m, plan)!;
  const [loading, setLoading] = useState<number | null>(null);
  const cap = act.capacityOf(m, plan) ?? 15;
  const byId = new Map(plan.stops.map((x) => [x.id, x]));
  const set = (next: Partial<act.Plan>) => ui.set({ plan: { ...plan, ...next } });
  // The same order and lengths as the map and the estimate: each run nearest next from the load point.
  const timed = act.planTimed(m, plan) as ReturnType<typeof act.runsStraight>;
  return (
    <div className="stops runs">
      <div className="row" style={{ gap: 6, padding: '0 16px 8px' }}>
        <span className="cap grow">{plural(rp.runs.length, 'run')} from {rp.depot && act.planStart(m, plan)?.label}; each run’s order is Google’s when it’s sent.</span>
        <span className="seg mini-seg" role="group" aria-label="How many runs">
          <button aria-pressed={plan.runs?.n == null} data-tip="As many as the vehicle needs" onClick={() => set({ runs: { n: null } })}>
            Fit
          </button>
          {[2, 3, 4].map((k) => (
            <button key={k} aria-pressed={plan.runs?.n === k} data-tip={`Shared out about evenly into ${k}`} onClick={() => set({ runs: { n: k } })}>
              {k}
            </button>
          ))}
        </span>
        <button className="mini" onClick={() => set({ runs: null })}>
          One run
        </button>
      </div>
      {rp.runs.map((run, r) => {
        const span = timed.runs[r];
        const order = timed.order.slice(span.start, span.start + span.count).map((id) => run.find((x) => x.id === id)!).filter(Boolean);
        const t = { distanceM: span.distance_m };
        const load = loadOfAll(run);
        return (
          <div key={r} className="run">
            <div className="runhead">
              <b>Run {r + 1}</b>
              <span className="cap">
                {plural(run.length, 'stop')} · {plural(load, 'parcel')}
                {load > cap ? ` · over the ${cap} it carries` : ''} · {length(t.distanceM)}
              </span>
              <span className="grow" />
              <button className="link cap" aria-expanded={loading === r} onClick={() => setLoading(loading === r ? null : r)}>
                {loading === r ? 'The stops' : 'Load list'}
              </button>
            </div>
            {loading === r ? (
              <div className="loadlist">
                <div className="cap" style={{ marginBottom: 4 }}>
                  {plural(packagesOf(run), 'package')} to load, the last drop first so the first is on top:
                </div>
                {loadList(order).map((x: RunStop) => (
                  <div key={x.id} className="row cap">
                    <span className="mono">{loadOf(x)}×</span>
                    <span className="grow ellipsis">{firstPart(byId.get(x.id)?.address ?? '')}</span>
                  </div>
                ))}
              </div>
            ) : (
              order.map((x, i) => {
                const ps = byId.get(x.id)!;
                return (
                  <div key={x.id} className="stop plan" style={{ '--c': 'var(--ink)', '--on': 'var(--surface)' } as React.CSSProperties}>
                    <span className="n">{two(span.start + i + 1)}</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="a ellipsis">{firstPart(ps.address)}</div>
                      <div className="s ellipsis">{[ps.area, ps.name, (ps.from?.packageCount as number | undefined) && (ps.from!.packageCount as number) > 1 ? `${ps.from!.packageCount} packages` : null].filter(Boolean).join(' · ') || ' '}</div>
                    </div>
                    <button className="iconbtn sm" data-tip="Take it off" aria-label="Take it off" onClick={() => set({ stops: plan.stops.filter((y) => y.id !== x.id), timed: null })}>
                      <Icon name="x" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Couriers for a new route: not on one first, nearest the start first; then those out, with when they'll be free. */
function whoTakes(m: Model, plan: act.Plan): { p: Person; busy: boolean; km: number | null; line: string }[] {
  const from = m.shop ?? plan.stops[0] ?? null;
  return m.people
    .map((p) => {
      const run = p.day?.running ?? null;
      const at = run?.position ?? p.day?.presence ?? null;
      const km = at && from ? metres(at, from) / 1000 : null;
      const waiting = p.day?.waiting[0];
      const done = p.day?.finished[0];
      const line = run
        ? `On the ${routeTitle(run, m.zone)}${p.day?.finishAt ? `, done about ${time(p.day.finishAt, m.zone)}` : ''}; this waits until then`
        : waiting
          ? `${routeTitle(waiting, m.zone)} waiting to start; this goes after it`
          : done
            ? `${done.state === 'ended' ? 'Ended' : 'Finished'} ${time(done.completedAt ?? done.endedAt, m.zone)}${km != null ? ` · ${length(km * 1000)} from ${m.shop ? 'the shop' : 'the first stop'}` : ''}`
            : `Not out yet${km != null ? ` · ${length(km * 1000)} from ${m.shop ? 'the shop' : 'the first stop'}` : ''}`;
      return { p, busy: !!run, km, line };
    })
    .sort((a, b) => Number(a.busy) - Number(b.busy) || (a.km ?? 99) - (b.km ?? 99));
}

// ── Who's closest to a spot (closest_couriers.dart, closest_sheet.dart) ──

/** How long a position counts as fresh (`CourierArea.freshFor`). */
const FRESH = 2 * 60 * 60 * 1000;

export function ClosestView({ m }: { m: Model }) {
  const s = useStore(ui);
  const c = s.closest!;
  const ranked = m.people
    .map((p) => {
      const run = p.day?.running ?? null;
      const at = run?.position ?? p.day?.presence ?? null;
      if (!at) return null;
      const seen = run ? (run.heardAt ?? run.startedAt) : (p.day?.presence?.at ?? null);
      return { p, run, rough: !run, seen, m: metres(at, c.at) };
    })
    .filter((x): x is NonNullable<typeof x> => !!x)
    .sort((a, b) => {
      const fa = a.seen != null && m.now - a.seen <= FRESH;
      const fb = b.seen != null && m.now - b.seen <= FRESH;
      return fa !== fb ? (fa ? -1 : 1) : a.m - b.m;
    });
  const none = m.people.filter((p) => !ranked.some((x) => x.p.uid === p.uid)).map((p) => p.first);
  const label = `Pickup · ${shortCode(c.at.lat, c.at.lng)}`;
  return (
    <>
      <div className="lhead">
        <div className="row">
          <button className="iconbtn sm" data-tip="Back to today · Esc" aria-label="Back" onClick={backToDay}>
            <Icon name="arrowLeft" size="lg" />
          </button>
          <div className="grow">
            <div className="title">Closest to this spot</div>
            <div className="cap">{ranked.length ? 'By straight-line distance, from where each courier last was.' : c.label}</div>
          </div>
        </div>
      </div>
      {ranked.length ? (
        <div className="rows">
          {ranked.map((x, i) => {
            const left = x.run ? x.run.stops.filter((y) => y.state === 'current' || y.state === 'ahead').length : 0;
            const fresh = x.seen != null && m.now - x.seen <= FRESH;
            const line = x.run ? `On a route · ${left ? `${plural(left, 'stop')} left` : 'on the way back'}` : fresh ? `Rough area · seen ${x.seen ? ago(x.seen, m.now) : 'a while ago'}` : `Seen ${x.seen ? ago(x.seen, m.now) : 'a while ago'} · may have moved`;
            return (
              <div key={x.p.uid} className="item" data-ctx={`courier:${x.p.uid}`}>
                <span className="mono" style={{ width: 14, fontSize: 12, lineHeight: '32px', flex: 'none', color: 'var(--ink2)' }}>
                  {i + 1}
                </span>
                <Av p={x.p} />
                <div className="grow">
                  <div className="row" style={{ gap: 8 }}>
                    <b>{x.p.first}</b>
                    {i === 0 && <span className="chip pos">Closest</span>}
                  </div>
                  <div className="cap ellipsis">{line}</div>
                  <div className="acts">
                    <button className="mini" onClick={() => act.sendPickup(x.p.uid, { lat: c.at.lat, lng: c.at.lng, address: label })}>
                      <Icon name="paperPlaneTilt" size="sm" />
                      Send the pickup to {x.p.first}
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flex: 'none' }}>
                  <div className="mono">
                    {x.rough ? '~' : ''}
                    {length(x.m)}
                  </div>
                  <div className="mono cap">about {inMinutes(x.m / (30000 / 3600) * 1000)}</div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyHelp
          title="None of your couriers are on a route or signed in with Routr open right now."
          tries={[
            { icon: 'plus', label: 'Start a new route here', note: 'Pick who takes it next', act: 'plan' },
            { icon: 'arrowLeft', label: 'Back to today', act: 'day' },
          ]}
          onTry={(a) => (a === 'plan' ? ui.set({ view: 'plan', plan: { stops: [{ id: crypto.randomUUID(), address: `Dropped pin · ${c.label}`, area: '', lat: c.at.lat, lng: c.at.lng, pin: true }], to: null, name: '' }, closest: null }) : backToDay())}
        />
      )}
      {none.length > 0 && <div className="cap" style={{ padding: '12px 16px 0' }}>No position for {none.join(', ')}: not on a route, and not signed in with Routr open.</div>}
      {ranked.some((x) => x.rough) && <div className="cap" style={{ padding: '8px 16px 0' }}>From a rough area (about 500 m across), so the distance is approximate. Times are at 30 km/h.</div>}
      <div style={{ height: 28 }} />
    </>
  );
}
